import React, { useState, useEffect } from 'react';
import { Layout, Menu } from 'antd';
import {
  TeamOutlined,
  AppstoreOutlined,
  ApartmentOutlined,
  CalendarOutlined,
  DownOutlined,
  SettingOutlined,
  UserOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { shouldShowMenuItem, shouldShowParentMenuItem } from '../../config/routePermissions';
import {useAuth} from "../../services";

const { Sider } = Layout;
type MenuItem = Required<MenuProps>['items'][number];

/**
 * CustomerSider Component
 *
 * Renders the collapsible sidebar with navigation menu.
 */
const CustomerSider: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo, isAuthenticated } = useAuth();
  const [menuItems, setMenuItems] = useState<MenuProps['items']>([]);

  // 獲取當前活躍的菜單項
  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/membership-master')) return ['membership-master'];
    if (path.startsWith('/membership/detail')) return ['membership-master'];
    if (path.startsWith('/product-master')) return ['product-master'];
    if (path.startsWith('/certificate-master')) return ['certificate-master'];
    if (path.startsWith('/price-certificate')) return ['price-certificate'];
    if (path.startsWith('/member-grade-check')) return ['member-grade-check'];
    if (path.startsWith('/member-statistics')) return ['member-statistics'];
    if (path.startsWith('/member-directory-export')) return ['member-directory-export'];
    if (path.startsWith('/zongjiu-export')) return ['zongjiu-export'];
    if (path.startsWith('/committee')) return ['committee'];
    if (path.startsWith('/association-activity')) return ['association-activity'];
    if (path.startsWith('/admin/users')) return ['admin-users'];
    if (path.startsWith('/admin/parameters')) return ['admin-parameters'];
    return [];
  };

  // 獲取當前打開的子菜單
  const getOpenKeys = () => {
    const path = location.pathname;
    const openKeys = [];

    if (path.startsWith('/membership-master') ||
        path.startsWith('/membership/detail') ||
        path.startsWith('/product-master') ||
        path.startsWith('/certificate-master')) {
      openKeys.push('membership-management');
    }

    if (path.startsWith('/price-certificate') ||
        path.startsWith('/member-grade-check') ||
        path.startsWith('/member-statistics') ||
        path.startsWith('/member-directory-export') ||
        path.startsWith('/zongjiu-export')) {
      openKeys.push('other');
    }

    if (path.startsWith('/committee')) {
      openKeys.push('professional-committee');
    }

    if (path.startsWith('/association-activity')) {
      openKeys.push('activity-management');
    }

    if (path.startsWith('/reports')) {
      openKeys.push('reports-management');
    }

    if (path.startsWith('/admin')) {
      openKeys.push('system-administration');
    }

    return openKeys;
  };

  // 處理菜單項點擊
  const handleMenuClick = (key: string) => {
    console.log('Menu item clicked:', key);

    switch (key) {
      case 'membership-master':
        navigate('/membership-master');
        break;
      case 'product-master':
        navigate('/product-master');
        break;
      case 'certificate-master':
        navigate('/certificate-master');
        break;
      case 'price-certificate':
        navigate('/price-certificate');
        break;
      case 'member-grade-check':
        navigate('/member-grade-check');
        break;
      case 'member-statistics':
        navigate('/member-statistics');
        break;
      case 'member-directory-export':
        navigate('/member-directory-export');
        break;
      case 'zongjiu-export':
        navigate('/zongjiu-export');
        break;
      case 'committee':
        navigate('/committee');
        break;
      case 'association-activity':
        navigate('/association-activity');
        break;
      case 'admin-users':
        navigate('/admin/users');
        break;
      case 'admin-parameters':
        navigate('/admin/parameters');
        break;
      default:
        break;
    }
  };

  // 生成菜單項的輔助函數
  function getItem(
    label: React.ReactNode,
    key: string,
    icon?: React.ReactNode,
    children?: MenuItem[],
    type?: 'group',
  ): MenuItem {
    return {
      key,
      icon,
      children,
      label,
      type,
    } as MenuItem;
  }

  // 根據用戶角色動態生成菜單項
  useEffect(() => {
    // 如果用户未登录，不显示任何菜单项
    if (!userInfo || !isAuthenticated) {
      setMenuItems([]);
      return;
    }

    const userRole = userInfo.role;
    const items: MenuProps['items'] = [];

    // 會員檔案維護與查詢
    if (shouldShowParentMenuItem('membership-management', userRole)) {
      const membershipChildren: MenuItem[] = [];

      if (shouldShowMenuItem('/membership-master', userRole)) {
        membershipChildren.push(getItem('會籍主檔', 'membership-master'));
      }

      if (shouldShowMenuItem('/product-master', userRole)) {
        membershipChildren.push(getItem('實際產品主檔', 'product-master'));
      }

      if (shouldShowMenuItem('/certificate-master', userRole)) {
        membershipChildren.push(getItem('認證主檔', 'certificate-master'));
      }

      if (membershipChildren.length > 0) {
        items.push(getItem('會員檔案維護與查詢', 'membership-management', <TeamOutlined />, membershipChildren));
      }
    }

    // 其他
    if (shouldShowParentMenuItem('other', userRole)) {
      const otherChildren: MenuItem[] = [];

      if (shouldShowMenuItem('/price-certificate', userRole)) {
        otherChildren.push(getItem('比價證明書', 'price-certificate'));
      }

      if (shouldShowMenuItem('/member-grade-check', userRole)) {
        otherChildren.push(getItem('會員級數錯誤檢驗', 'member-grade-check'));
      }

      if (shouldShowMenuItem('/member-statistics', userRole)) {
        otherChildren.push(getItem('會員組織統計', 'member-statistics'));
      }
      
      if (shouldShowMenuItem('/member-directory-export', userRole)) {
        otherChildren.push(getItem('會員名錄匯出', 'member-directory-export'));
      }
      if (shouldShowMenuItem('/zongjiu-export', userRole)) {
        otherChildren.push(getItem('會員資料轉宗九匯出', 'zongjiu-export'));
      }

      if (otherChildren.length > 0) {
        items.push(getItem('其他', 'other', <AppstoreOutlined />, otherChildren));
      }
    }

    // 專業委員會
    if (shouldShowParentMenuItem('professional-committee', userRole)) {
      const committeeChildren: MenuItem[] = [];

      if (shouldShowMenuItem('/committee', userRole)) {
        committeeChildren.push(getItem('專委會', 'committee'));
      }

      if (committeeChildren.length > 0) {
        items.push(getItem('專業委員會', 'professional-committee', <ApartmentOutlined />, committeeChildren));
      }
    }

    // 活動維護
    if (shouldShowParentMenuItem('activity-management', userRole)) {
      const activityChildren: MenuItem[] = [];

      if (shouldShowMenuItem('/association-activity', userRole)) {
        activityChildren.push(getItem('本會活動維護', 'association-activity'));
      }

      if (activityChildren.length > 0) {
        items.push(getItem('活動維護', 'activity-management', <CalendarOutlined />, activityChildren));
      }
    }

    if (shouldShowParentMenuItem('system-administration', userRole)) {
      // 系統管理設定
      const adminChildren: MenuItem[] = [];
      if (shouldShowMenuItem('/admin-users', userRole)) {
      adminChildren.push(getItem('使用者設定', 'admin-users', <UserOutlined />));
      }

      if (shouldShowMenuItem('/admin-parameters', userRole)) {
      adminChildren.push(getItem('參數設定', 'admin-parameters', <ToolOutlined />));
      }

      items.push(getItem('系統管理設定', 'system-administration', <SettingOutlined />, adminChildren));
    }

    setMenuItems(items);
  }, [userInfo, isAuthenticated]);

  // 如果用户未登录，不显示侧边栏
  if (!isAuthenticated || !userInfo) {
    return null;
  }

  return (
    <Sider
      style={{
        height: 'calc(100vh - 112px)', // 減去 Header 和 Footer 的高度
        background: '#001529',
        zIndex: 5,
        overflowY: 'auto'
      }}
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      breakpoint="lg"
      theme="dark"
      width={250}
      collapsedWidth={80}
    >
      <div style={{ padding: '16px 0' }}>
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={getSelectedKeys()}
          defaultOpenKeys={getOpenKeys()}
          style={{ height: '100%', borderRight: 0 }}
          onClick={({ key }) => handleMenuClick(key)}
          items={menuItems}
          expandIcon={({ isOpen }) => <DownOutlined rotate={isOpen ? 180 : 0} />}
        />
      </div>
    </Sider>
  );
};

export default CustomerSider;
